import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, Flame, CheckCircle2, Zap, Target, ChevronRight, CreditCard, RefreshCw, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { toast } from "sonner";
import MirrorScore from "@/components/MirrorScore";
import { useMirrorInsight } from "@/hooks/useMirrorInsight";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import { useAvatarSnapshot } from "@/hooks/useAvatarSnapshot";
import { CHECKIN_DAY_BASIS, checkInDayKey, shiftCheckInDay } from "@/lib/checkInDates";
import { useBodyMeasurements } from '@/hooks/useBodyMeasurements';
import { BodyHealthActionModal, BodyHealthPendingActionLauncher } from '@/components/mirror/BodyHealthActionModal';
import { newBodyHealthOperationId, type BodyHealthActionPrefill } from '@/lib/bodyHealthExecutor';

const calcStreak = (checkins: any[]) => {
  if (!checkins.length) return 0;
  const today = checkInDayKey(new Date())!;
  const dates = checkins.flatMap((c) => c.checked_in_at ? [checkInDayKey(c.checked_in_at)] : [])
    .filter((day): day is string => day !== null && day <= today);
  const unique = [...new Set(dates)].sort().reverse();
  if (!unique.length) return 0;
  let streak = 0;
  // Same today-or-yesterday grace as the Check-in module, in its canonical day.
  const anchor = unique[0] === today ? today : shiftCheckInDay(today, -1);
  for (let i = 0; i < unique.length; i++) {
    if (unique[i] === shiftCheckInDay(anchor, -i)) streak++;
    else break;
  }
  return streak;
};

const Progress = () => {
  const navigate = useNavigate();
  const { t, tf, tp, lang } = useLanguage();
  const { snapshotUrl, hasAvatar } = useAvatarSnapshot();
  const [profile, setProfile] = useState<any>(null);
  const [checkIns, setCheckIns] = useState<any[]>([]);
  const [weightInput, setWeightInput] = useState("");
  const body = useBodyMeasurements();
  const [bodyAction, setBodyAction] = useState<BodyHealthActionPrefill | null>(null);
  const savingWeight = !!bodyAction;
  const [gaugeAnimated, setGaugeAnimated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [habitsCount, setHabitsCount] = useState({ done: 0, total: 0 });
  const [goalsCount, setGoalsCount] = useState(0);
  const [disciplineScore, setDisciplineScore] = useState<number | null>(null);
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const weightHistory = useMemo(() => body.measurements.filter(row => typeof row.weight_kg === 'number').slice(0, 5)
    .map(row => ({ id:row.id, date: new Date(row.measured_at).toLocaleDateString(localeTagFor(lang), { month: 'short', day: 'numeric' }), weight: row.weight_kg })), [body.measurements, lang]);
  useEffect(() => { setBodyAction(null); setWeightInput(''); }, [body.userId, body.identityGeneration]);
  const { insight, loading: insightLoading, regenerating, regenerate, canRegenerate, regenerationsUsed } = useMirrorInsight();
  const { isReported } = useReportedContent();
  // Destaque denunciado some do Progresso e nao volta no reload. A semana
  // seguinte gera outra linha, com outro id — o corte e daquele texto.
  const insightHidden = isReported(REPORT_SURFACES.WEEKLY_INSIGHT, insight?.id);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        const { data: p } = await supabase.from("user_profiles").select("*").eq("user_id", user.id).maybeSingle();
        const { data: c } = await supabase.from("daily_checkins").select("*").eq("user_id", user.id)
          .not("checked_in_at", "is", null).lte("checked_in_at", new Date().toISOString())
          .order("checked_in_at", { ascending: false }).limit(14);
        setProfile(p);
        setCheckIns(c || []);
        // Load real habits count
        try {
          const { data: habits } = await (supabase as any).from("habits").select("id, is_active").eq("user_id", user.id).eq("is_active", true);
          const { data: completions } = await (supabase as any).from("habit_completions").select("id").eq("user_id", user.id).gte("completed_date", new Date().toISOString().split("T")[0]);
          setHabitsCount({ done: completions?.length || 0, total: habits?.length || 0 });
        } catch (err) { console.error('[Progress] load habits/completions failed:', err); }
        // Load real goals count
        try {
          const { data: goals } = await (supabase as any).from("goals").select("id").eq("user_id", user.id).eq("is_completed", false);
          setGoalsCount(goals?.length || 0);
        } catch (err) { console.error('[Progress] load goals failed:', err); }
        // Discipline score: completions this week / (active habits * 7) * 100
        try {
          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          const { data: activeHabits } = await (supabase as any).from("habits").select("id").eq("user_id", user.id).eq("is_active", true);
          const { data: weekCompletions } = await (supabase as any).from("habit_completions").select("id").eq("user_id", user.id).gte("completed_date", weekAgo);
          const activeCount = (activeHabits || []).length;
          const compCount = (weekCompletions || []).length;
          const expected = activeCount * 7;
          setDisciplineScore(expected > 0 ? Math.round((compCount / expected) * 100) : null);
        } catch (err) { console.error('[Progress] compute discipline score failed:', err); }
        // Confidence score: average progress % on active goals
        try {
          const { data: goalProgress } = await (supabase as any).from("goals").select("progress_override").eq("user_id", user.id).eq("is_completed", false);
          const withProgress = (goalProgress || []).filter((g: any) => g.progress_override != null);
          setConfidenceScore(withProgress.length > 0
            ? Math.round(withProgress.reduce((sum: number, g: any) => sum + (g.progress_override || 0), 0) / withProgress.length)
            : null);
        } catch (err) { console.error('[Progress] compute confidence score failed:', err); }
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    load();
    setTimeout(() => setGaugeAnimated(true), 400);
  }, []);

  const avgMetric = (key: string, arr = checkIns.slice(0, 7)) => {
    if (!arr.length) return 0;
    return +(arr.reduce((s, c) => s + (c[key] || 0), 0) / arr.length).toFixed(1);
  };

  const weeklyScore = (() => {
    const dims: number[] = [];
    if (checkIns.length > 0) {
      dims.push(avgMetric("mood") * 20);
      dims.push(avgMetric("energy") * 20);
    }
    if (disciplineScore !== null) dims.push(disciplineScore);
    if (confidenceScore !== null) dims.push(confidenceScore);
    return dims.length > 0 ? Math.round(dims.reduce((a, b) => a + b, 0) / dims.length) : 0;
  })();

  const lastWeekScore = (() => {
    const prev = checkIns.slice(7, 14);
    if (!prev.length) return null;
    return Math.round(
      ((avgMetric("mood", prev) + avgMetric("energy", prev)) / 2) * 20
    );
  })();

  const streak = calcStreak(checkIns);
  const weekCheckins = checkIns.filter((c) => {
    const day = c.checked_in_at ? checkInDayKey(c.checked_in_at) : null;
    const today = checkInDayKey(new Date())!;
    return day !== null && day >= shiftCheckInDay(today, -6) && day <= today;
  }).length;

  const scoreColor = weeklyScore >= 70 ? "hsl(187 72% 50%)" : weeklyScore >= 50 ? "hsl(40 90% 60%)" : "hsl(0 60% 55%)";

  // SVG gauge
  const gaugeRadius = 54;
  const gaugeCircumference = 2 * Math.PI * gaugeRadius;
  const gaugePct = checkIns.length ? weeklyScore / 100 : 0;
  const gaugeOffset = gaugeCircumference * (1 - (gaugeAnimated ? gaugePct : 0));

  // Energy trend: latest seven check-ins, not necessarily consecutive days.
  const energyRecords = checkIns.slice(0, 7).reverse();
  const energyValues = energyRecords.map((c) => (c.energy || 0) * 20);
  const energyDates = energyRecords.map((c) => new Date(c.checked_in_at).toLocaleDateString(localeTagFor(lang), { day: "2-digit", month: "2-digit", timeZone: CHECKIN_DAY_BASIS }));
  const avgEnergy = energyValues.length ? Math.round(energyValues.reduce((s, v) => s + v, 0) / energyValues.length) : 0;

  // Area progress (simulated percentages based on check-in data)
  const moodPct = checkIns.length > 0 ? Math.round(avgMetric("mood") * 20) : 0;
  const energyPct = checkIns.length > 0 ? Math.round(avgMetric("energy") * 20) : 0;
  const disciplinePct = disciplineScore !== null ? Math.round(disciplineScore) : 0;
  const areas = [
    { emoji: "🧠", label: t("progress.areaMind"), pct: moodPct, color: "hsl(280 60% 60%)", route: "/notes" },
    { emoji: "💪", label: t("progress.areaBody"), pct: energyPct, color: "hsl(140 50% 50%)", route: "/fitness" },
    { emoji: "💰", label: t("progress.areaLife"), pct: disciplinePct, color: "hsl(45 90% 55%)", route: "/goals" },
    { emoji: "📚", label: t("progress.areaGrowth"), pct: habitsCount.total > 0 ? Math.round((habitsCount.done / habitsCount.total) * 100) : 0, color: "hsl(210 60% 55%)", route: "/habits" },
  ];


  const moodEmojis = ["😔", "😐", "🙂", "😊", "🔥"];

  const handleLogWeight = () => {
    if (!/^\d+(?:[.,]\d+)?$/.test(weightInput.trim())) return;
    const weight = Number(weightInput.trim().replace(',', '.'));
    if (!Number.isFinite(weight) || weight < 20 || weight > 300 || !body.userId || !body.capture()()) return;
    setBodyAction({ resource: 'body', action: 'create', operation_id: newBodyHealthOperationId(), action_id: newBodyHealthOperationId(), expected_user_id: body.userId,
      fields: { weight_kg: weight, measured_at: new Date().toISOString() },
    });
  };

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

  const MIN_CHECKINS = 3;
  const hasEnoughData = checkIns.length >= MIN_CHECKINS;
  const checkinsRemaining = MIN_CHECKINS - checkIns.length;

  return (
    <MobileLayout className="pb-24 pt-8 relative overflow-hidden">
      <div className="absolute top-[5%] left-1/2 -translate-x-1/2 w-[300px] h-[200px] rounded-full opacity-[0.04] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, hsl(187 72% 50%), transparent 70%)" }} />

      <header className="relative z-10 flex items-center gap-3 mb-5">
        <MirrorBackButton />
        <div><p className="vyv-label mb-2">{t("progress.yourEvolution")}</p>
          <h1 className="font-display text-[24px] font-semibold text-foreground leading-tight">{t("progress.title")}</h1></div>
      </header>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-foreground/60 text-[14px] font-display mb-3">{t("progress.loadError")}</p>
          {/* PRESERVE: page fetches data via raw useEffect([]) (line 45),
              not TanStack Query — soft refresh requires extracting load() into
              a callback or migrating the page to useQuery. Out of scope for
              Capacitor prep; deferred to W3 TanStack migration. */}
          <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-xl bg-primary/10 border border-primary/20 text-primary text-[12px] font-display">
            {t("common.retry")}
          </button>
        </div>
      )}

      {!loading && !error && (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="space-y-5 relative z-10">



        {/* Profile Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)} className="vyv-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl overflow-hidden bg-primary/10 flex items-center justify-center">
                {hasAvatar && snapshotUrl ? (
                  <img
                    src={snapshotUrl}
                    alt={profile?.first_name || "Avatar"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="font-display text-[15px] font-semibold text-primary/90">
                    {(profile?.first_name || "U").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <p className="font-display text-[15px] font-semibold text-foreground">{profile?.first_name || t("common.you")}</p>
                <p className="text-muted-foreground text-[11px] font-display tracking-wide">
                  {[profile?.age && `${profile.age}y`, profile?.height_cm && `${profile.height_cm}cm`].filter(Boolean).join(" · ") || t("settings.yourProfile")}
                </p>
              </div>
            </div>
            <button onClick={() => navigate("/settings/profile")}
              className="text-primary/60 text-[11px] font-display font-medium flex items-center gap-0.5 hover:text-primary/80 transition-colors">
              {t("progress.editProfile")} <ChevronRight size={12} />
            </button>
          </div>
        </motion.div>

        {/* Mirror Score */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}
          className="vyv-card p-6">
          <p className="vyv-label mb-4">{hasEnoughData ? t("ux.progress.title") : t("progress.collectingData")}</p>
          {hasEnoughData ? (
            <MirrorScore
              score={weeklyScore}
              contributors={[
                { name: t("score.mood"),     value: Math.round(avgMetric("mood") * 20),       icon: "Smile",        route: "/check-in",   hasData: checkIns.length > 0 },
                { name: t("score.energy"),   value: Math.round(avgMetric("energy") * 20),     icon: "Zap",          route: "/check-in",   hasData: checkIns.length > 0 },
                { name: t("score.habits"),   value: disciplineScore ?? 0, icon: "CheckSquare", route: "/habits", hasData: disciplineScore !== null },
                { name: t("score.goals"),    value: confidenceScore !== null ? confidenceScore : 0, icon: "Flag",    route: "/goals",      hasData: confidenceScore !== null },
              ]}
              mirrorMessage={t("ux.progress.summary")}
              loading={loading}
            />
          ) : (
            <div className="flex flex-col items-center py-6">
              <div className="relative w-[130px] h-[130px] mb-4" style={{ opacity: 0.25 }}>
                <svg width="130" height="130" viewBox="0 0 130 130">
                  <circle cx="65" cy="65" r="54" fill="none" stroke="hsl(222 18% 14%)" strokeWidth="8" />
                </svg>
              </div>
              <p className="font-display text-[18px] font-bold text-foreground/40">{checkIns.length} / {MIN_CHECKINS}</p>
              <p className="text-muted-foreground text-[11px] font-display mt-1">{t("progress.learningAboutYou")}</p>
            </div>
          )}
          <details className="mt-4 rounded-xl border border-border/50 bg-background/40 px-4 py-3">
            <summary className="cursor-pointer rounded text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              {t("ux.progress.how")}
            </summary>
            <ul className="mt-3 list-disc space-y-2 pl-4 text-sm leading-relaxed text-muted-foreground">
              <li>{t("ux.progress.checkins")}</li>
              <li>{t("ux.progress.habits")}</li>
              <li>{t("ux.progress.goals")}</li>
              <li>{t("ux.progress.limits")}</li>
            </ul>
          </details>
          <button
            onClick={() => navigate("/score-history")}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium tracking-wide hover:bg-primary/15 transition-colors"
          >
            {t("score.history")}
          </button>
          <button
            onClick={() => navigate("/vyv-moments")}
            className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-primary/20 text-primary/60 text-[12px] font-display font-medium tracking-wide hover:bg-primary/5 transition-colors"
          >
            <Sparkles size={12} />
            {t("moments.viewMoments")}
          </button>
          <button
            onClick={() => navigate("/identity-card")}
            className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all active:scale-[0.98]"
            style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.25)", color: "hsl(var(--primary))" }}
          >
            <CreditCard size={13} />
            <span className="text-[12px] font-display font-semibold tracking-wide">{t("progress.identityCard")}</span>
          </button>
        </motion.div>

        {/* 2x2 Metric Grid */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)}
          className="grid grid-cols-2 gap-3">
          <div className="vyv-card p-4">
            <Flame size={15} className="text-primary/60 mb-2" />
            <p className="font-display text-[22px] font-bold text-foreground leading-none">{streak}</p>
            <p className="text-muted-foreground text-[10px] font-display tracking-wide mt-1">{t("progress.dayStreak")}</p>
          </div>
          <div className="vyv-card p-4">
            <CheckCircle2 size={15} className="text-primary/60 mb-2" />
            <p className="font-display text-[22px] font-bold text-foreground leading-none">{habitsCount.done} / {habitsCount.total}</p>
            <p className="text-muted-foreground text-[10px] font-display tracking-wide mt-1">{t("progress.habitsDone")}</p>
          </div>
          <div className="vyv-card p-4">
            <Zap size={15} className="text-accent/60 mb-2" />
            <p className="font-display text-[22px] font-bold text-foreground leading-none">{weekCheckins}</p>
            <p className="text-muted-foreground text-[10px] font-display tracking-wide mt-1">{t("progress.checkinsWeek")}</p>
          </div>
          <div className="vyv-card p-4">
            <Target size={15} className="text-primary/60 mb-2" />
            <p className="font-display text-[22px] font-bold text-foreground leading-none">{goalsCount}</p>
            <p className="text-muted-foreground text-[10px] font-display tracking-wide mt-1">{t("progress.goalsActive")}</p>
          </div>
        </motion.div>

        {/* Progress by Area */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)}
          className="vyv-card p-5">
          <p className="vyv-label mb-4">{t("progress.byArea")}</p>
          <div className="space-y-3.5">
            {areas.map((area) => (
              <button key={area.label} onClick={() => navigate(area.route)}
                className="w-full flex items-center gap-3 group">
                <span className="text-[16px] shrink-0">{area.emoji}</span>
                <div className="flex-1">
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="font-display text-[12px] font-medium text-foreground/80 group-hover:text-foreground transition-colors">{area.label}</span>
                    <span className="text-[10px] font-display font-semibold" style={{ color: area.color }}>{area.pct}%</span>
                  </div>
                  <div className="w-full h-[5px] bg-muted/30 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${area.pct}%` }}
                      transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                      className="h-full rounded-full" style={{ background: area.color }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Energy Trend */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)}
          className="vyv-card p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="vyv-label">{t("progress.energyTrend")}</p>
            {energyValues.length > 0 && <span className="text-muted-foreground text-xs font-display">{tf("progress.avg", { value: String(avgEnergy) })}</span>}
          </div>
          <div className="relative h-[80px]">
            {energyValues.length >= 2 ? (
              <svg aria-hidden="true" width="100%" height="80" viewBox="0 0 280 80" preserveAspectRatio="none" className="overflow-visible">
                {/* Grid lines */}
                {[0, 40, 80].map((y) => (
                  <line key={y} x1="0" y1={y} x2="280" y2={y} stroke="hsl(222 18% 20%)" strokeWidth="0.5" />
                ))}
                {/* Line */}
                <polyline fill="none" stroke="hsl(187 72% 50%)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  points={energyValues.map((v, i) => `${(i / (energyValues.length - 1)) * 280},${80 - (v / 100) * 76}`).join(" ")} />
                {/* Points */}
                {energyValues.map((v, i) => (
                  <circle key={i} cx={(i / (energyValues.length - 1)) * 280} cy={80 - (v / 100) * 76}
                    r="4" fill="hsl(187 72% 50%)" stroke="hsl(222 38% 6%)" strokeWidth="2" />
                ))}
              </svg>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground/50 text-[11px] font-display">{t("progress.needMore")}</p>
              </div>
            )}
          </div>
          {energyValues.length >= 2 && (
            <div className="flex justify-between mt-2 px-0.5">
              {energyDates.map((d, i) => (
                <time key={i} dateTime={energyRecords[i].checked_in_at} className="text-[11px] text-muted-foreground font-display">{d}</time>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t("ux.progress.energyOrder")}</p>
          {energyValues.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer rounded text-sm text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t("ux.progress.values")}</summary>
              <table className="mt-2 w-full text-left text-sm">
                <caption className="sr-only">{t("progress.energyTrend")}</caption>
                <thead><tr><th scope="col" className="py-2">{t("ux.progress.date")}</th><th scope="col" className="py-2">{t("score.energy")}</th></tr></thead>
                <tbody>{energyRecords.map((record, i) => (
                  <tr key={record.id ?? i} className="border-t border-border/30"><td className="py-2">{energyDates[i]}</td><td className="py-2">{energyValues[i]}%</td></tr>
                ))}</tbody>
              </table>
            </details>
          )}
        </motion.div>

        {/* Collecting data hint (only when not enough data) */}
        {!hasEnoughData && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(5)}
            className="vyv-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={13} className="text-primary/40" />
              <p className="vyv-label">{t("progress.collectingData")}</p>
            </div>
            <p className="text-foreground/50 text-[12px] font-body leading-relaxed">
              {tp("progress.checkinsRemaining", checkinsRemaining)}
            </p>
          </motion.div>
        )}

        {/* Mirror Insights (real, from AI) */}
        {hasEnoughData && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(5)}
            className="vyv-card p-5 border border-primary/15">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={13} className="text-primary/60" />
                <p className="vyv-label">{t("insights.title")}</p>
              </div>
              {insight && (
                <button
                  onClick={async () => {
                    if (!canRegenerate) { toast.error(t("insights.regenerateLimit")); return; }
                    toast.info(t("insights.regeneratingNow"));
                    var result = await regenerate();
                    if (result?.error === "rate_limit") toast.error(t("insights.regenerateLimit"));
                  }}
                  disabled={!canRegenerate || regenerating}
                  className="flex items-center gap-1 text-[9px] font-display text-primary/40 hover:text-primary/60 transition-colors disabled:opacity-30"
                >
                  <RefreshCw size={10} className={regenerating ? "animate-spin" : ""} />
                  {t("insights.regenerate")}
                </button>
              )}
            </div>

            {insightLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : insight && !insightHidden ? (
              <>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <p className="text-foreground/70 text-[13px] font-body italic leading-relaxed flex-1">
                    {insight.highlight_text}
                  </p>
                  {/* Denuncia do destaque semanal. O texto e sintese do Claude
                      sobre a semana da pessoa — vai junto com os badges e a
                      acao recomendada no snapshot, porque a moderacao precisa
                      ver o card inteiro, nao so a primeira frase. */}
                  <ReportContentButton
                    surface={REPORT_SURFACES.WEEKLY_INSIGHT}
                    contentId={insight.id}
                    snapshot={[
                      insight.highlight_text,
                      insight.badges.length > 0 ? insight.badges.join(" / ") : "",
                      insight.recommended_action ?? "",
                    ].filter(Boolean).join("\n\n")}
                    context={{ generated_at: insight.generated_at }}
                  />
                </div>
                {insight.badges.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {insight.badges.map((b: string, i: number) => (
                      <span key={i} className="text-[9px] font-display px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary/70">
                        {b}
                      </span>
                    ))}
                  </div>
                )}
                {insight.recommended_action && (
                  <button
                    onClick={() => insight.recommended_action_route ? navigate(insight.recommended_action_route) : null}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors"
                  >
                    <ArrowRight size={12} />
                    {insight.recommended_action}
                  </button>
                )}
                {import.meta.env.DEV && (
                  <p className="text-muted-foreground/30 text-[9px] font-display mt-2">
                    {tf("insights.regenerationsUsed", { used: String(regenerationsUsed) })}
                    {insight.generation_cost_usd !== null ? " · $" + insight.generation_cost_usd.toFixed(4) : ""}
                  </p>
                )}
              </>
            ) : (
              <p className="text-foreground/40 text-[12px] font-body italic">
                {t("insights.generating")}
              </p>
            )}
          </motion.div>
        )}

        {/* Body Progress */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(6)}
          className="vyv-card p-5">
          <p className="vyv-label mb-4">{t("progress.bodyProgress")}</p>
          {profile?.user_id === body.userId && profile?.current_weight_kg && (
            <div className="flex gap-3 mb-4">
              <div className="flex-1 vyv-metric p-3">
                <p className="text-foreground font-display font-semibold text-[16px]">{profile.current_weight_kg}<span className="text-[10px] text-muted-foreground ml-0.5">kg</span></p>
                <p className="text-muted-foreground text-[9px] uppercase tracking-[0.2em] mt-0.5 font-display">{t("common.current")}</p>
              </div>
              {profile?.target_weight_kg && (
                <div className="flex-1 vyv-metric p-3">
                  <p className="text-foreground font-display font-semibold text-[16px]">{profile.target_weight_kg}<span className="text-[10px] text-muted-foreground ml-0.5">kg</span></p>
                  <p className="text-muted-foreground text-[9px] uppercase tracking-[0.2em] mt-0.5 font-display">{t("common.target")}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 mb-4">
            <input type="number" step="0.1" min="20" max="300" value={weightInput} onChange={(e) => setWeightInput(e.target.value)}
              placeholder={t("progress.weightPlaceholder")}
              className="flex-1 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl px-3 py-2.5 text-[13px] font-body text-foreground/70 placeholder:text-foreground/25 focus:outline-none" />
            <button onClick={handleLogWeight} disabled={savingWeight || !weightInput || !body.hasData || body.error || body.isRefreshing}
              className="px-4 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors disabled:opacity-40">
              {t("progress.logWeight")}
            </button>
          </div>
          {body.userId && <BodyHealthPendingActionLauncher ownerId={body.userId} busy={!!bodyAction} />}
          {weightHistory.length > 0 && (
            <div className="space-y-2">
              {weightHistory.map((entry) => (
                <div key={entry.id} className="flex justify-between items-center py-1.5 border-b border-border/10 last:border-0">
                  <span className="text-muted-foreground text-[11px] font-display">{entry.date}</span>
                  <span className="text-foreground/70 text-[12px] font-display font-medium">{entry.weight} kg</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Check-ins */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(7)}>
          <p className="vyv-label mb-3">{t("progress.recent")}</p>
          {checkIns.length === 0 ? (
            <div className="vyv-card p-5 text-center">
              <p className="text-foreground/70 text-[13px] font-display">{t("progress.noCheckins")}</p>
              <p className="text-muted-foreground text-[11px] mt-1">{t("progress.startTracking")}</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {checkIns.slice(0, 7).map((c) => (
                <div key={c.id} className="vyv-card p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="text-[16px]">{moodEmojis[(c.mood || 3) - 1]}</span>
                      <p className="text-foreground text-[12px] font-display font-medium">
                        {new Date(c.checked_in_at).toLocaleDateString(localeTagFor(lang), { weekday: "short", month: "short", day: "numeric", timeZone: CHECKIN_DAY_BASIS })}
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <span className={`px-2.5 py-1 rounded-full text-[9px] font-display font-medium ${
                        (c.energy || 0) >= 4 ? "bg-emerald-500/15 text-emerald-400" :
                        (c.energy || 0) >= 3 ? "bg-yellow-500/15 text-yellow-400" :
                        "bg-destructive/15 text-destructive/70"
                      }`}>⚡ {c.energy}/5</span>
                    </div>
                  </div>
                  {c.note && <p className="text-muted-foreground text-[11px] mt-2 leading-relaxed font-body">{c.note}</p>}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
      )}

      <BodyHealthActionModal open={!!bodyAction} prefill={bodyAction} onClose={() => setBodyAction(null)} onCreated={() => undefined}
        onConfirmed={() => { if (body.capture()()) { setBodyAction(null); setWeightInput(''); } }} />
    </MobileLayout>
  );
};

export default Progress;
